import React, { useEffect, useState } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "../store";
import companiesService from "../features/companies/companiesService";
import MainLayout from "../components/layout/MainLayout";
import LoginPage from "../pages/auth/LoginPage";
// import RegisterPage from "../pages/auth/RegisterPage";
import VerificationPage from "../pages/auth/VerificationPage";
import ForgotPasswordPage from "../pages/auth/ForgotPasswordPage";
import ResetPasswordPage from "../pages/auth/ResetPasswordPage";
import OnboardingPage from "../pages/auth/OnboardingPage";
import DashboardPage from "../pages/dashboard/DashboardPage";
import UsersPage from "../pages/users/UsersPage";
import UserProfilePage from "../pages/users/UserProfilePage";
import CompaniesPage from "../pages/companies/CompaniesPage";
import MarketingDashboard from "../pages/dashboard/MarketingDashboard";
import AIChatbotsPage from "../pages/ai_chatbots/AIChatbotsPage";
import AIChatbotTrainingPage from "../pages/ai_chatbots/AIChatbotTrainingPage";
import AIChatbotsPageEdit from "../pages/ai_chatbots/AIChatbotsPageEdit";
import CommunicationsHubPage from "../pages/marketing_dashboard/CommunicationsHubPage";
import CompanyResearchPage from "../pages/marketing_dashboard/CompanyResearchPage";
import EmailMarketingPage from "../pages/marketing_dashboard/EmailMarketingPage";
import LeadGenerationPage from "../pages/marketing_dashboard/LeadGenerationPage";
import SocialMediaManagement from "../pages/marketing_dashboard/SocialMediaManagement";
import WhatsappChatbotsPage from "../pages/marketing_dashboard/WhatsappChatbotsPage";
import WhatsappMarketingPage from "../pages/marketing_dashboard/WhatsappMarketingPage";
import AgencyProfile from "../pages/my_agency/AgencyProfile";
import ClientCompaniesPage from "../pages/my_agency/ClientCompaniesPage";
import MyTeamPage from "../pages/my_agency/MyTeamPage";
import UsagePage from "../pages/usage/UsagePage";
import AIToolsPage from "../pages/ai_tools/AIToolsPage";
import AIToolsChatbotsPage from "../pages/ai_tools/AIToolsChatbotsPage";
import DeepResearchPage from "../pages/ai_tools/DeepResearchPage";
import WhatsappChatbotsToolsPage from "../pages/ai_tools/WhatsappChatbotsPage";
import MyProfilePage from "../pages/profile/MyProfilePage";
import UnauthorizedPage from "../pages/error/UnauthorizedPage";
import ForbiddenPage from "../pages/error/ForbiddenPage";
import NotFoundPage from "../pages/error/NotFoundPage";
import AdminOnlyRoute from "../components/AdminOnlyRoute";
import BmsRoute from "../components/BmsRoute";
import AdminOrAgencyOwnerRoute from "../components/AdminOrAgencyOwnerRoute";
import PersonalOnlyRoute from "../components/PersonalOnlyRoute";
// ... diğer importlar
import MarketingHome from "../pages/marketing/MarketingHome";
import PrivacyPolicyPage from "../pages/legal/PrivacyPolicyPage";
import TermsOfServicePage from "../pages/legal/TermsOfServicePage";
import PublicLayout from "../layouts/PublicLayout";
import PersonalChatPage from "../pages/personal/PersonalChatPage";
import PricingPage from "../pages/subscriptions/PricingPage";
import SubscriptionPage from "../pages/subscriptions/SubscriptionPage";
import SubscriptionSuccessPage from "../pages/subscriptions/SubscriptionSuccessPage";
import PlansManagementPage from "../pages/subscriptions/admin/PlansManagementPage";
import SubscriptionsDashboardPage from "../pages/subscriptions/admin/SubscriptionsDashboardPage";
import PaymentsPage from "../pages/subscriptions/admin/PaymentsPage";
import AcceptInvitationPage from "../pages/subscriptions/AcceptInvitationPage";
import BmsBookingsPage from "../pages/bms/BmsBookingsPage";
import BmsCalendarPage from "../pages/bms/BmsCalendarPage";
import BmsUsersPage from "../pages/bms/BmsUsersPage";
import BmsCustomersPage from "../pages/bms/BmsCustomersPage";
import BmsServicesPage from "../pages/bms/BmsServicesPage";
import BmsBookingConfigurationsPage from "../pages/bms/BmsBookingConfigurationsPage";
import BmsMyAvailabilityPage from "../pages/bms/BmsMyAvailabilityPage";
import ModuleManagementPage from "../pages/admin/ModuleManagementPage";
import ModuleCatalogPage from "../pages/admin/ModuleCatalogPage";
import AgentPublicPage from "../pages/public/AgentPublicPage";

// Legal sayfaları için dinamik layout seçici
const LegalRouteWrapper: React.FC = () => {
  const isAuthenticated = localStorage.getItem("token") !== null;

  if (isAuthenticated) {
    return <MainLayout />;
  }
  return <PublicLayout />;
};

// Auth kontrolü için koruyucu bileşen
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const isAuthenticated = localStorage.getItem("token") !== null;
  const location = useLocation();
  const { user } = useSelector((state: RootState) => state.auth);
  const [isChecking, setIsChecking] = useState(true);
  const [redirectTo, setRedirectTo] = useState<string | null>(null);

  useEffect(() => {
    if (!isAuthenticated) {
      setIsChecking(false);
      return;
    }

    // Allow profile setup pages to render without redirect loop
    const pathname = location.pathname;
    if (
      pathname.startsWith("/my-agency/profile") ||
      pathname.startsWith("/my-company/profile") ||
      pathname.startsWith("/onboarding")
    ) {
      setRedirectTo(null);
      setIsChecking(false);
      return;
    }

    const roles = user?.roles || [];
    const isAgencyOwner = roles.includes("agency_owner");
    const isCompanyOwner = roles.includes("company_owner");

    // Specialists don't need to go through onboarding
    const isSpecialist = roles.includes("company_specialist") || roles.includes("marketing_specialist");

    const requiresCompany = (isAgencyOwner || isCompanyOwner) && !isSpecialist;

    if (!requiresCompany) {
      setRedirectTo(null);
      setIsChecking(false);
      return;
    }

    const checkCompany = async () => {
      try {
        // honor local storage hint to avoid flicker
        const setupHint = localStorage.getItem('companySetupRequired');
        if (setupHint === 'agency') {
          throw new Error('setup-required');
        }
        if (setupHint === 'single') {
          throw new Error('setup-required');
        }
        if (isAgencyOwner) {
          await companiesService.getMyAgency();
        } else if (isCompanyOwner) {
          await companiesService.getMyCompany();
        }
        setRedirectTo(null);
      } catch (e) {
        // persist setup requirement to avoid flicker on sidebar clicks
        localStorage.setItem('companySetupRequired', isAgencyOwner ? 'agency' : 'single');
        setRedirectTo(
          isAgencyOwner ? "/my-agency/profile?type=agency" : "/my-company/profile?type=single"
        );
      } finally {
        setIsChecking(false);
      }
    };

    checkCompany();
  }, [isAuthenticated, location.pathname, user]);

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (isChecking) {
    return null;
  }

  if (redirectTo && location.pathname !== redirectTo) {
    return <Navigate to={redirectTo} replace />;
  }

  return <>{children}</>;
};

const AppRoutes: React.FC = () => {
  const location = useLocation();

  useEffect(() => {
    const gtag = (window as any).gtag as
      | ((...args: any[]) => void)
      | undefined;

    if (!gtag) {
      return;
    }

    // Conversion event ayarları
    const sendTo =
      process.env.REACT_APP_GA_CONVERSION_SEND_TO

    // Conversion sayfası olarak mantıklı olmayan path'leri hariç tut
    const excludedPrefixes = [
      "/login",
      "/verify-email",
      "/forgot-password",
      "/reset-password",
      "/401",
      "/403",
      "/404",
    ];

    const isExcluded = excludedPrefixes.some((prefix) =>
      location.pathname.startsWith(prefix)
    );

    if (isExcluded) {
      return;
    }

    gtag("event", "conversion", {
      send_to: sendTo,
      value: 1.0,
      currency: "GBP",
    });
  }, [location.pathname]);

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={<LoginPage />} />
      {/* Self-service registration is disabled; users are created by admins.
      <Route path="/register" element={<RegisterPage />} />
      */}
      <Route path="/verify-email" element={<VerificationPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />

      {/* Public Legal Pages - Login durumuna göre otomatik layout seçer */}
      <Route element={<LegalRouteWrapper />}>
        <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
        <Route path="/terms-of-service" element={<TermsOfServicePage />} />
      </Route>

      {/* Protected Onboarding Route */}
      <Route
        path="/onboarding"
        element={
          <ProtectedRoute>
            <OnboardingPage />
          </ProtectedRoute>
        }
      />

      {/* Protected routes */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <MainLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="dashboard" element={<DashboardPage />} />
        <Route path="users" element={<UsersPage />} />
        <Route
          path="users/:userId"
          element={
            <AdminOnlyRoute>
              <UserProfilePage />
            </AdminOnlyRoute>
          }
        />
        <Route
          path="marketing-dashboard"
          element={
            <AdminOrAgencyOwnerRoute>
              <MarketingDashboard />
            </AdminOrAgencyOwnerRoute>
          }
        />
        <Route
          path="marketing-dashboard/communications-hub"
          element={
            <AdminOnlyRoute>
              <CommunicationsHubPage />
            </AdminOnlyRoute>
          }
        />
        <Route
          path="marketing-dashboard/company-research"
          element={
            <AdminOnlyRoute>
              <CompanyResearchPage />
            </AdminOnlyRoute>
          }
        />
        <Route
          path="marketing-dashboard/email-marketing"
          element={
            <AdminOnlyRoute>
              <EmailMarketingPage />
            </AdminOnlyRoute>
          }
        />
        <Route
          path="marketing-dashboard/lead-generation"
          element={
            <AdminOnlyRoute>
              <LeadGenerationPage />
            </AdminOnlyRoute>
          }
        />
        <Route
          path="marketing-dashboard/social-media-management"
          element={
            <AdminOnlyRoute>
              <SocialMediaManagement />
            </AdminOnlyRoute>
          }
        />
        <Route
          path="marketing-dashboard/whatsapp-chatbots"
          element={
            <AdminOnlyRoute>
              <WhatsappChatbotsPage />
            </AdminOnlyRoute>
          }
        />
        <Route
          path="marketing-dashboard/whatsapp-marketing"
          element={
            <AdminOnlyRoute>
              <WhatsappMarketingPage />
            </AdminOnlyRoute>
          }
        />



        {/* Agent routes (placeholder) */}
        <Route path="chatbots">
          <Route index element={<div>Agents List</div>} />
          <Route path="new" element={<div>Create New AI Agent</div>} />
          <Route path=":id" element={<div>Agent Details</div>} />
          <Route path=":id/edit" element={<div>Edit Agent</div>} />
          <Route path="analytics" element={<div>Agent Analytics</div>} />
        </Route>

        {/* Company routes */}
        <Route path="companies">
          <Route index element={<CompaniesPage />} />
          <Route path="new" element={<CompaniesPage isNew={true} />} />
          <Route path=":id" element={<CompaniesPage isDetail={true} />} />
          <Route path=":id/edit" element={<CompaniesPage isEdit={true} />} />
        </Route>

        {/* Report routes */}
        <Route path="reports">
          <Route index element={<div>Reports</div>} />
          <Route path="generate" element={<div>Generate Report</div>} />
        </Route>

        {/* Usage route */}
        <Route path="usage" element={<UsagePage />} />

        {/* Subscription routes (company_owner, agency_owner) */}
        <Route path="pricing" element={<PricingPage />} />
        <Route path="subscription" element={<SubscriptionPage />} />
        <Route path="subscription/success" element={<SubscriptionSuccessPage />} />
        <Route path="subscription/cancel" element={<PricingPage />} />
        <Route path="subscription/accept-invitation" element={<AcceptInvitationPage />} />

        {/* Admin subscription routes */}
        <Route
          path="admin/module-management"
          element={
            <AdminOnlyRoute>
              <ModuleManagementPage />
            </AdminOnlyRoute>
          }
        />
        <Route
          path="admin/module-catalog"
          element={
            <AdminOnlyRoute>
              <ModuleCatalogPage />
            </AdminOnlyRoute>
          }
        />
        <Route
          path="admin/plans"
          element={
            <AdminOnlyRoute>
              <PlansManagementPage />
            </AdminOnlyRoute>
          }
        />
        <Route
          path="admin/subscriptions"
          element={
            <AdminOnlyRoute>
              <SubscriptionsDashboardPage />
            </AdminOnlyRoute>
          }
        />
        <Route
          path="admin/payments"
          element={
            <AdminOnlyRoute>
              <PaymentsPage />
            </AdminOnlyRoute>
          }
        />

        {/* AI Tools */}
        <Route path="ai-tools">
          <Route index element={<AIToolsPage />} />
          <Route path="deep-research" element={<DeepResearchPage />} />
          <Route path="whatsapp-chatbots" element={<WhatsappChatbotsToolsPage />} />
        </Route>

        <Route path="ai-chatbots">
          <Route index element={<AIChatbotsPage />} />
          <Route path="new" element={<AIChatbotsPageEdit />} />
          <Route path=":id/edit" element={<AIChatbotsPageEdit />} />
          <Route path=":id/training" element={<AIChatbotTrainingPage />} />
        </Route>
        <Route path="bms">
          <Route path="users" element={<BmsRoute><BmsUsersPage /></BmsRoute>} />
          <Route path="customers" element={<BmsRoute><BmsCustomersPage /></BmsRoute>} />
          <Route path="services" element={<BmsRoute><BmsServicesPage /></BmsRoute>} />
          <Route path="bookings" element={<BmsRoute><BmsBookingsPage /></BmsRoute>} />
          <Route path="booking-configurations" element={<BmsRoute><BmsBookingConfigurationsPage /></BmsRoute>} />
          <Route path="calendar" element={<BmsRoute><BmsCalendarPage /></BmsRoute>} />
          <Route path="my-availability" element={<BmsRoute><BmsMyAvailabilityPage /></BmsRoute>} />
        </Route>
        {/* Marketing - Admin/Superadmin only (mockup pages) */}
        <Route
          path="marketing"
          element={
            <AdminOnlyRoute>
              <MarketingHome />
            </AdminOnlyRoute>
          }
        />
        <Route
          path="marketing/communications-hub"
          element={
            <AdminOnlyRoute>
              <CommunicationsHubPage />
            </AdminOnlyRoute>
          }
        />
        <Route
          path="marketing/company-research"
          element={
            <AdminOnlyRoute>
              <CompanyResearchPage />
            </AdminOnlyRoute>
          }
        />
        <Route
          path="marketing/email-marketing"
          element={
            <AdminOnlyRoute>
              <EmailMarketingPage />
            </AdminOnlyRoute>
          }
        />
        <Route
          path="marketing/lead-generation"
          element={
            <AdminOnlyRoute>
              <LeadGenerationPage />
            </AdminOnlyRoute>
          }
        />
        <Route
          path="marketing/social-media-management"
          element={
            <AdminOnlyRoute>
              <SocialMediaManagement />
            </AdminOnlyRoute>
          }
        />
        <Route
          path="marketing/whatsapp-chatbots"
          element={
            <AdminOnlyRoute>
              <WhatsappChatbotsPage />
            </AdminOnlyRoute>
          }
        />
        <Route
          path="marketing/whatsapp-marketing"
          element={
            <AdminOnlyRoute>
              <WhatsappMarketingPage />
            </AdminOnlyRoute>
          }
        />

        {/* Settings route */}
        <Route path="settings" element={<div>Settings</div>} />

        {/* Profile route */}
        <Route path="profile" element={<MyProfilePage />} />

        {/* My Agency routes */}
        <Route path="my-agency">
          <Route index element={<div>My Agency</div>} />
          <Route
            path="profile"
            element={
              <ProtectedRoute>
                <AgencyProfile />
              </ProtectedRoute>
            }
          />
          <Route
            path="my-team"
            element={
              <ProtectedRoute>
                <MyTeamPage />
              </ProtectedRoute>
            }
          />
        </Route>

        {/* My Company routes for company_owner (reuse agency components) */}
        <Route path="my-company">
          <Route index element={<Navigate to="profile" replace />} />
          <Route
            path="profile"
            element={
              <ProtectedRoute>
                <AgencyProfile />
              </ProtectedRoute>
            }
          />
          <Route
            path="my-team"
            element={
              <ProtectedRoute>
                <MyTeamPage />
              </ProtectedRoute>
            }
          />
        </Route>

        {/* Client Companies routes */}
        <Route
          path="client-companies"
          element={
            <ProtectedRoute>
              <ClientCompaniesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="personal/chat"
          element={
            <PersonalOnlyRoute>
              <PersonalChatPage />
            </PersonalOnlyRoute>
          }
        />
      </Route>

      {/* Error Routes */}
      <Route path="/401" element={<UnauthorizedPage />} />
      <Route path="/403" element={<ForbiddenPage />} />
      <Route path="/404" element={<NotFoundPage />} />

      {/*
        Public agent page served at meylo.app/{company_slug}/{widget_key}.
        Must be registered AFTER all literal two-segment routes so React Router
        v6 specificity prefers them (e.g. /ai-chatbots/new), and BEFORE the
        catch-all so that unknown two-segment URLs still render the public
        agent page component which handles its own 404 state via the backend.
      */}
      <Route path="/:companySlug/:widgetKey" element={<AgentPublicPage />} />

      {/* Catch all - 404 */}
      <Route path="*" element={<Navigate to="/404" replace />} />
    </Routes>
  );
};

export default AppRoutes;
